import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketDivider, MarketHeader, MarketModalPartial } from '@market/react';
import OptionRows, {
  Options,
} from 'typescript/components/option_rows/option_rows';
import { buildOptionFromPickupOption } from 'typescript/utilities/pickup_options';
import { useListPickupOptions } from 'typescript/hooks/use_product_client';
import EmptyStateWithLoading from 'typescript/components/empty_state_with_loading';
import PostalCodeSearchInput from 'typescript/components/postal_code_search_input';
import { PickupOption } from 'typescript/types/api/pickup_options';

import styles from 'typescript/pages/product/bopis/pickup_options_modal.module.scss';

interface PickupOptionsModalProps {
  handleClose: () => void;
  slug: string;
  postalCode: string;
  setPostalCode: (postalCode: string) => void;
  chosenPickupLocationId: string | null;
  setChosenPickupLocationId: (id: string | null) => void;
  currentPickupOptions: PickupOption[];
  setCurrentPickupOptions: (options: PickupOption[]) => void;
}

export const INPUT_TEST_ID = 'input-test-id';

const PickupOptionsModal = ({
  handleClose,
  slug,
  postalCode,
  setPostalCode,
  chosenPickupLocationId,
  setChosenPickupLocationId,
  currentPickupOptions,
  setCurrentPickupOptions,
}: PickupOptionsModalProps) => {
  const { t } = useTranslation();

  const { pickupOptionsData, isFetching, refetch } = useListPickupOptions({
    productSlug: slug,
    postalCode,
  });

  useEffect(() => {
    if (pickupOptionsData) {
      setCurrentPickupOptions(pickupOptionsData);
    }

    // Clear chosen pickup location if it's not in the new options list
    if (pickupOptionsData && chosenPickupLocationId) {
      const hasMatchingLocation = pickupOptionsData.some(
        (pickupOption) =>
          pickupOption.location_id.toString() === chosenPickupLocationId,
      );

      if (!hasMatchingLocation) {
        setChosenPickupLocationId(null);
      }
    }
  }, [pickupOptionsData]);

  const onPickupLocationSelect = ({ optionId }) => {
    setChosenPickupLocationId(optionId);
  };

  const pickupLocationOptions: Options[] = useMemo(() => {
    if (!currentPickupOptions?.length) return null;
    if (isFetching) return [];

    return currentPickupOptions.map((pickupOption, _index) =>
      buildOptionFromPickupOption({
        pickupOption,
        chosenPickupLocationId: chosenPickupLocationId?.toString(),
      }),
    );
  }, [currentPickupOptions]);

  // TODO (bdang): Replace hardcoded "Enter your ZIP code..." with translation
  return (
    <MarketModalPartial
      onMarketDialogDismissed={handleClose}
      data-market-features="typography,html-styles"
      className={styles.modal}
    >
      <MarketHeader showNavigation onMarketHeaderNavigate={handleClose}>
        <h1>{t('checkout.delivery.pickup.chooseStore')}</h1>
      </MarketHeader>
      <div className={`shop-grid-container ${styles.container}`}>
        <h4 className="shop-grid-item-full">
          {t('productPage.pickup.enterZip')}
        </h4>
        <div className={`shop-grid-item-full`}>
          <PostalCodeSearchInput
            value={postalCode}
            onValueChange={setPostalCode}
            onButtonClick={refetch}
            showSearchButton={true}
            className="shop-grid-item-full"
            inputTestId={INPUT_TEST_ID}
          />
        </div>
        {pickupLocationOptions && !isFetching ? (
          <div className="shop-grid-item-full no-grid-gap">
            <OptionRows
              className="shop-grid-item-full"
              options={pickupLocationOptions}
              onOptionSelect={onPickupLocationSelect}
            />
            {pickupLocationOptions?.length > 0 && (
              <div className="shop-grid-item-full">
                <MarketDivider size="thin" />
                <p className={`${styles.disclaimer} shop-grid-item-full`}>
                  {t('checkout.delivery.pickup.disclaimer')}
                </p>
              </div>
            )}
          </div>
        ) : (
          <EmptyStateWithLoading
            className={`${styles.noOptions} shop-grid-item-full`}
            showLoading={isFetching}
            description={t('checkout.delivery.pickup.noAvailableStores')}
          />
        )}
      </div>
    </MarketModalPartial>
  );
};

export default PickupOptionsModal;
